import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { AUTH_TOKEN_KEY, registerAccount, loginAccount, fetchMe } from "@/lib/api";

const AuthContext = createContext(null);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  // user: undefined = checking, null = anonymous, object = signed in
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      setUser(null);
      return;
    }
    fetchMe()
      .then((u) => setUser(u))
      .catch(() => {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        setUser(null);
      });
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { token, user: u } = await loginAccount({ email, password });
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      setUser(u);
      return { ok: true, user: u };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    try {
      const { token, user: u } = await registerAccount({ email, password, name });
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      setUser(u);
      return { ok: true, user: u };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    // Also drop the client-side student-session resume pointers so signing out and re-entering (e.g. as Guest)
    // does NOT silently restore the previous essay. Deliberate resume is still available from "My Work".
    try {
      localStorage.removeItem("compass_student_session");
      localStorage.removeItem("compass_ot_session");
    } catch (e) { /* ignore */ }
    setUser(null);
    window.location.assign("/");   // sign out → the home page, never a sign-in form
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthed: !!user, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
